import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FaMusic, FaPlay, FaClock, FaToggleOn, FaToggleOff, FaTrash, FaPlus } from 'react-icons/fa';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import KpiCard from '../components/KpiCard/KpiCard.jsx';
import cacheArtistasApi from '../services/cacheArtistasApi.js';
import { colors } from '../utils/colors.js';

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0 0 24px;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 2px solid ${colors.gray[100]};
  margin-bottom: 20px;
`;

const Tab = styled.button`
  padding: 10px 20px;
  border: none;
  background: transparent;
  color: ${(props) => (props.$active ? colors.primary[600] : colors.gray[500])};
  font-weight: ${(props) => (props.$active ? '600' : '400')};
  font-size: 14px;
  border-bottom: 2px solid ${(props) => (props.$active ? colors.primary[600] : 'transparent')};
  margin-bottom: -2px;
  cursor: pointer;

  &:hover {
    color: ${colors.primary[600]};
  }
`;

const Card = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.gray[700]};
  margin-bottom: 6px;
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 13px;
  min-height: 100px;
  margin-bottom: 16px;
  font-family: inherit;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${colors.gradients.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  background: ${colors.error.light};
  color: ${colors.error.dark};
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const SuccessBox = styled.div`
  background: ${colors.success.light};
  color: ${colors.success.dark};
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${colors.gray[500]};
  margin: -10px 0 16px;
`;

const List = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const ExecRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid ${colors.gray[100]};
  gap: 16px;

  &:last-child {
    border-bottom: none;
  }
`;

const ExecMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ExecTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Small = styled.span`
  font-size: 13px;
  color: ${colors.gray[500]};
`;

const StatusBadge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(props) => {
    if (props.$status === 'completed') return colors.success.light;
    if (props.$status === 'failed') return colors.error.light;
    if (props.$status === 'partial') return colors.warning.light;
    return colors.info.light;
  }};
  color: ${(props) => {
    if (props.$status === 'completed') return colors.success.dark;
    if (props.$status === 'failed') return colors.error.dark;
    if (props.$status === 'partial') return colors.warning.dark;
    return colors.info.dark;
  }};
`;

const Empty = styled.div`
  padding: 40px;
  text-align: center;
  color: ${colors.gray[500]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const ChartTitle = styled.h2`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.gray[800]};
  margin: 0 0 16px;
`;

const STATUS_LABELS = {
  running: 'Em execução',
  completed: 'Concluída',
  failed: 'Falhou',
  partial: 'Parcial',
};

function money(v) {
  return (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatPeriodo(exec) {
  if (!exec.data_inicial && !exec.data_final) return 'Período: todo o histórico';
  const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('pt-BR');
  return `Período: ${exec.data_inicial ? fmt(exec.data_inicial) : '…'} – ${exec.data_final ? fmt(exec.data_final) : '…'}`;
}

// ---------------------------------------------------------------------------
// Disparar Execução
// ---------------------------------------------------------------------------

function DispararExecucao({ onCriada }) {
  const [diarios, setDiarios] = useState([]);
  const [diarioSlug, setDiarioSlug] = useState('');
  const [termos, setTermos] = useState('');
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  useEffect(() => {
    cacheArtistasApi.listDiarios().then((data) => {
      const lista = data?.diarios || [];
      setDiarios(lista);
      const primeiroDisponivel = lista.find((d) => d.conector_disponivel);
      if (primeiroDisponivel) {
        setDiarioSlug(primeiroDisponivel.slug);
      }
    }).catch(() => setDiarios([]));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setSucesso('');
    setEnviando(true);
    try {
      const termosBusca = termos
        .split('\n')
        .map((t) => t.trim())
        .filter(Boolean);
      const payload = {
        diario_slug: diarioSlug,
        termos_busca: termosBusca.length > 0 ? termosBusca : null,
        data_inicial: dataInicial || null,
        data_final: dataFinal || null,
      };
      const execucao = await cacheArtistasApi.criarExecucao(payload);
      setSucesso(`Execução ${execucao.id} disparada — acompanhe na aba "Execuções".`);
      onCriada?.();
    } catch (err) {
      setErro(err.response?.data?.detail || 'Não foi possível disparar a execução.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Card as="form" onSubmit={handleSubmit}>
      {erro && <ErrorBox>{erro}</ErrorBox>}
      {sucesso && <SuccessBox>{sucesso}</SuccessBox>}

      <Label htmlFor="diario">Diário</Label>
      <Select id="diario" value={diarioSlug} onChange={(e) => setDiarioSlug(e.target.value)} required>
        <option value="" disabled>Selecione um diário</option>
        {diarios.map((d) => (
          <option key={d.slug} value={d.slug} disabled={!d.conector_disponivel}>
            {d.tipo === 'prefeitura' ? `Prefeitura de ${d.municipio} (${d.uf})` : `Governo do Estado (${d.uf})`}
            {!d.conector_disponivel ? ' — sem conector' : ''}
          </option>
        ))}
      </Select>

      <Label htmlFor="termos">Termos de busca (um por linha, opcional — usa os termos padrão do diário se vazio)</Label>
      <Textarea
        id="termos"
        value={termos}
        onChange={(e) => setTermos(e.target.value)}
        placeholder={'Contrato Artístico\napresentação artística\n...'}
      />

      <Row>
        <div>
          <Label htmlFor="data_inicial">Data inicial (opcional)</Label>
          <Input id="data_inicial" type="date" value={dataInicial} onChange={(e) => setDataInicial(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="data_final">Data final (opcional)</Label>
          <Input id="data_final" type="date" value={dataFinal} onChange={(e) => setDataFinal(e.target.value)} />
        </div>
      </Row>
      <Hint>Sem datas, a busca cobre todo o histórico disponível no diário — pode ser lenta. Prefira um intervalo curto para o primeiro teste.</Hint>

      <Button type="submit" disabled={enviando || !diarioSlug}>
        <FaPlay /> {enviando ? 'Disparando...' : 'Disparar execução'}
      </Button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Execuções
// ---------------------------------------------------------------------------

function Execucoes({ reloadKey }) {
  const [execucoes, setExecucoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const pollingRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await cacheArtistasApi.listExecucoes(30);
      setExecucoes(data?.execucoes || []);
    } catch (err) {
      // silencioso: a lista simplesmente fica vazia
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  useEffect(() => {
    const temExecucaoRodando = execucoes.some((e) => e.status === 'running');
    if (temExecucaoRodando && !pollingRef.current) {
      pollingRef.current = setInterval(load, 4000);
    } else if (!temExecucaoRodando && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [execucoes, load]);

  return (
    <List>
      {loading && <Empty>Carregando...</Empty>}
      {!loading && execucoes.length === 0 && <Empty>Nenhuma execução disparada ainda.</Empty>}
      {!loading && execucoes.map((exec) => (
        <ExecRow key={exec.id}>
          <ExecMeta>
            <ExecTop>
              <Small>{new Date(exec.iniciado_em).toLocaleString('pt-BR')}</Small>
              <StatusBadge $status={exec.status}>{STATUS_LABELS[exec.status] || exec.status}</StatusBadge>
              {exec.agendamento_id && (
                <StatusBadge $status="info" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <FaClock size={10} /> Via agendamento
                </StatusBadge>
              )}
            </ExecTop>
            <Small>{formatPeriodo(exec)}</Small>
            <Small>
              {exec.paginas_processadas}/{exec.paginas_encontradas} páginas ·{' '}
              {exec.eventos_criados} evento(s) · {exec.apresentacoes_criadas} apresentação(ões) ·{' '}
              {money(exec.custo_usd)}
            </Small>
            {exec.erro && <Small style={{ color: colors.error.dark }}>{exec.erro}</Small>}
          </ExecMeta>
        </ExecRow>
      ))}
    </List>
  );
}

// ---------------------------------------------------------------------------
// Insights
// ---------------------------------------------------------------------------

function Insights() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cacheArtistasApi.getInsights().then(setInsights).catch(() => setInsights(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Empty>Carregando...</Empty>;
  if (!insights) return <Empty>Não foi possível carregar os insights.</Empty>;

  return (
    <div>
      <Grid>
        <KpiCard icon={FaMusic} label="Eventos" value={insights.total_eventos} color={colors.primary[600]} bg={colors.primary[100]} />
        <KpiCard icon={FaMusic} label="Apresentações" value={insights.total_apresentacoes} color={colors.primary[600]} bg={colors.primary[100]} />
        <KpiCard icon={FaMusic} label="Cachê total" value={money(insights.total_cache)} color={colors.success.dark} bg={colors.success.light} />
        <KpiCard icon={FaMusic} label="Cachê médio" value={money(insights.cache_medio)} color={colors.success.dark} bg={colors.success.light} />
      </Grid>

      <Card>
        <ChartTitle>Top 15 artistas por cachê total</ChartTitle>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={insights.top_artistas} layout="vertical" margin={{ left: 100 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tickFormatter={(v) => money(v)} />
            <YAxis type="category" dataKey="artista_nome_artistico" width={140} />
            <Tooltip formatter={(v) => money(v)} />
            <Bar dataKey="total_cache" fill={colors.primary[500]} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card>
        <ChartTitle>Cachê total por cidade</ChartTitle>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={insights.cache_por_cidade}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="cidade" interval={0} angle={-25} textAnchor="end" height={70} />
            <YAxis tickFormatter={(v) => money(v)} />
            <Tooltip formatter={(v) => money(v)} />
            <Bar dataKey="total_cache" fill={colors.info.main} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card>
        <ChartTitle>Distribuição por faixa de valor</ChartTitle>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={insights.distribuicao_valor}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="faixa" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="quantidade" fill={colors.warning.main} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agendamento
// ---------------------------------------------------------------------------

function Agendamentos({ onChanged }) {
  const [diarios, setDiarios] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diarioSlug, setDiarioSlug] = useState('');
  const [termos, setTermos] = useState('');
  const [intervaloHoras, setIntervaloHoras] = useState(24);
  const [janelaDias, setJanelaDias] = useState(3);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const loadAgendamentos = useCallback(async () => {
    try {
      const data = await cacheArtistasApi.listAgendamentos();
      setAgendamentos(data?.agendamentos || []);
    } catch (err) {
      // silencioso: a lista simplesmente fica vazia
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cacheArtistasApi.listDiarios().then((data) => {
      const lista = data?.diarios || [];
      setDiarios(lista);
      const primeiroDisponivel = lista.find((d) => d.conector_disponivel);
      if (primeiroDisponivel) setDiarioSlug(primeiroDisponivel.slug);
    }).catch(() => setDiarios([]));
    loadAgendamentos();
  }, [loadAgendamentos]);

  const diarioLabel = (diarioId) => {
    const d = diarios.find((x) => x.id === diarioId);
    if (!d) return diarioId;
    return d.tipo === 'prefeitura' ? `Prefeitura de ${d.municipio} (${d.uf})` : `Governo do Estado (${d.uf})`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setSucesso('');
    setEnviando(true);
    try {
      const termosBusca = termos.split('\n').map((t) => t.trim()).filter(Boolean);
      const payload = {
        diario_slug: diarioSlug,
        termos_busca: termosBusca.length > 0 ? termosBusca : null,
        intervalo_horas: Number(intervaloHoras),
        janela_dias: Number(janelaDias),
        enabled: true,
      };
      await cacheArtistasApi.criarAgendamento(payload);
      setSucesso('Agendamento criado.');
      setTermos('');
      await loadAgendamentos();
      onChanged?.();
    } catch (err) {
      setErro(err.response?.data?.detail || 'Não foi possível criar o agendamento.');
    } finally {
      setEnviando(false);
    }
  };

  const handleToggle = async (ag) => {
    setErro('');
    try {
      await cacheArtistasApi.atualizarAgendamento(ag.id, { enabled: !ag.enabled });
      await loadAgendamentos();
    } catch (err) {
      setErro(err.response?.data?.detail || 'Não foi possível atualizar o agendamento.');
    }
  };

  const handleDelete = async (ag) => {
    if (!window.confirm('Remover este agendamento? Essa ação não pode ser desfeita.')) return;
    setErro('');
    try {
      await cacheArtistasApi.removerAgendamento(ag.id);
      await loadAgendamentos();
    } catch (err) {
      setErro(err.response?.data?.detail || 'Não foi possível remover o agendamento.');
    }
  };

  return (
    <div>
      <Card as="form" onSubmit={handleSubmit}>
        {erro && <ErrorBox>{erro}</ErrorBox>}
        {sucesso && <SuccessBox>{sucesso}</SuccessBox>}

        <Label htmlFor="ag_diario">Diário</Label>
        <Select id="ag_diario" value={diarioSlug} onChange={(e) => setDiarioSlug(e.target.value)} required>
          <option value="" disabled>Selecione um diário</option>
          {diarios.map((d) => (
            <option key={d.slug} value={d.slug} disabled={!d.conector_disponivel}>
              {d.tipo === 'prefeitura' ? `Prefeitura de ${d.municipio} (${d.uf})` : `Governo do Estado (${d.uf})`}
              {!d.conector_disponivel ? ' — sem conector' : ''}
            </option>
          ))}
        </Select>

        <Label htmlFor="ag_termos">Termos de busca (um por linha, opcional — usa os termos padrão do diário se vazio)</Label>
        <Textarea
          id="ag_termos"
          value={termos}
          onChange={(e) => setTermos(e.target.value)}
          placeholder={'Contrato Artístico\napresentação artística\n...'}
        />

        <Row>
          <div>
            <Label htmlFor="ag_intervalo">Intervalo (horas)</Label>
            <Input
              id="ag_intervalo" type="number" min={1} value={intervaloHoras}
              onChange={(e) => setIntervaloHoras(e.target.value)} required
            />
          </div>
          <div>
            <Label htmlFor="ag_janela">Janela de busca (dias)</Label>
            <Input
              id="ag_janela" type="number" min={1} value={janelaDias}
              onChange={(e) => setJanelaDias(e.target.value)} required
            />
          </div>
        </Row>
        <Hint>
          A cada {intervaloHoras || '—'}h, dispara automaticamente uma busca cobrindo os últimos{' '}
          {janelaDias || '—'} dia(s) a partir do momento do disparo (não é uma expressão cron —
          sem controle de horário/dia da semana específico).
        </Hint>

        <Button type="submit" disabled={enviando || !diarioSlug}>
          <FaPlus /> {enviando ? 'Criando...' : 'Criar agendamento'}
        </Button>
      </Card>

      <List>
        {loading && <Empty>Carregando...</Empty>}
        {!loading && agendamentos.length === 0 && <Empty>Nenhum agendamento criado ainda.</Empty>}
        {!loading && agendamentos.map((ag) => (
          <ExecRow key={ag.id}>
            <ExecMeta>
              <ExecTop>
                <Small style={{ fontWeight: 600, color: colors.gray[800] }}>{diarioLabel(ag.diario_id)}</Small>
                <StatusBadge $status={ag.enabled ? 'completed' : 'failed'}>
                  {ag.enabled ? 'Ativo' : 'Pausado'}
                </StatusBadge>
              </ExecTop>
              <Small>
                A cada {ag.intervalo_horas}h · janela de {ag.janela_dias} dia(s) · próxima execução:{' '}
                {new Date(ag.proxima_execucao_em).toLocaleString('pt-BR')}
              </Small>
            </ExecMeta>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                type="button" onClick={() => handleToggle(ag)}
                style={{ background: colors.gray[100], color: colors.gray[700] }}
                title={ag.enabled ? 'Pausar' : 'Reativar'}
              >
                {ag.enabled ? <FaToggleOn /> : <FaToggleOff />}
              </Button>
              <Button
                type="button" onClick={() => handleDelete(ag)}
                style={{ background: colors.error.light, color: colors.error.dark }}
                title="Remover"
              >
                <FaTrash />
              </Button>
            </div>
          </ExecRow>
        ))}
      </List>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function CacheArtistasPage() {
  const [activeTab, setActiveTab] = useState('executar');
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <div>
      <Title>Cache Artistas</Title>
      <TabsContainer>
        <Tab type="button" $active={activeTab === 'executar'} onClick={() => setActiveTab('executar')}>
          Disparar Execução
        </Tab>
        <Tab type="button" $active={activeTab === 'agendamento'} onClick={() => setActiveTab('agendamento')}>
          Agendamento
        </Tab>
        <Tab type="button" $active={activeTab === 'execucoes'} onClick={() => setActiveTab('execucoes')}>
          Execuções
        </Tab>
        <Tab type="button" $active={activeTab === 'insights'} onClick={() => setActiveTab('insights')}>
          Insights
        </Tab>
      </TabsContainer>

      {activeTab === 'executar' && (
        <DispararExecucao onCriada={() => { setActiveTab('execucoes'); setReloadKey((k) => k + 1); }} />
      )}
      {activeTab === 'agendamento' && <Agendamentos />}
      {activeTab === 'execucoes' && <Execucoes reloadKey={reloadKey} />}
      {activeTab === 'insights' && <Insights />}
    </div>
  );
}
